/**
 * Custom hook to get authentication token for admin API requests
 *
 * Returns Clerk session token for authenticated requests.
 * When VITE_ADMIN_DEV_BYPASS=true (local preview without Clerk), returns
 * a mock implementation that skips auth entirely.
 */

import { adminApiFetch, type AdminApiFetchInit } from "@/lib/adminApiFetch";
import { useAuth } from "@clerk/clerk-react";
import { useCallback } from "react";

// Evaluated once at module load — Vite replaces import.meta.env.* at build time.
// This means we export the SAME function reference on every render, satisfying rules of hooks.
const DEV_BYPASS = import.meta.env.VITE_ADMIN_DEV_BYPASS === "true";

/** Real implementation — requires Clerk provider */
function useAdminAuthClerk() {
  const { isLoaded, isSignedIn, getToken } = useAuth();

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    if (!isSignedIn) {
      throw new Error("No autenticado — iniciá sesión para continuar");
    }

    let token = await getToken();
    if (!token) {
      // Clerk puede tardar un tick después de isLoaded/isSignedIn en el primer paint admin.
      await new Promise((resolve) => setTimeout(resolve, 150));
      token = await getToken({ skipCache: true });
    }

    if (!token) {
      throw new Error("No autenticado — no se pudo obtener el token de sesión");
    }

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [getToken, isSignedIn]);

  const adminFetch = useCallback(
    (input: RequestInfo | URL, init?: AdminApiFetchInit) =>
      adminApiFetch(getAuthHeaders, input, init),
    [getAuthHeaders],
  );

  return { getAuthHeaders, adminFetch, isLoaded, isSignedIn };
}

/** Mock implementation — used when VITE_ADMIN_DEV_BYPASS=true (no Clerk in local dev) */
function useAdminAuthMock() {
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => ({
    "Content-Type": "application/json",
  }), []);

  const adminFetch = useCallback(
    (input: RequestInfo | URL, init?: AdminApiFetchInit) =>
      adminApiFetch(getAuthHeaders, input, init),
    [getAuthHeaders],
  );

  return {
    getAuthHeaders,
    adminFetch,
    isLoaded: true as const,
    isSignedIn: false as const,
  };
}

// Export the right hook variant. Because DEV_BYPASS is a compile-time constant,
// the exported symbol is always the same function reference — no conditional hook calls.
export const useAdminAuth = DEV_BYPASS ? useAdminAuthMock : useAdminAuthClerk;
